import { Box, Link, Text, VStack } from '@chakra-ui/react'

import { FEEDBACK_FORM_URL } from '~constants/config'

import { PageSection } from '../components/pageSection'

export function PrivacyPage() {
  const updatedDate = '30 Jan 2024'

  return (
    <>
      <PageSection backgroundColor="base.canvas.brand-subtle">
        <Box width="100%" paddingX={{ base: '0px', lg: '55px', xl: '100px' }}>
          <Box maxW="550px" paddingY="112px">
            <Text textStyle="h1" marginBottom="16px" color="base.content.brand">
              Privacy Statement
            </Text>
            <Text textStyle="body-1">Updated as of {updatedDate}</Text>
          </Box>
        </Box>
      </PageSection>
      <PageSection backgroundColor="base.canvas.default">
        <VStack
          paddingTop="72px"
          paddingBottom="124px"
          marginX={{ base: '20px', md: '30px', lg: '55px', xl: '100px' }}
          as="section"
          position="relative"
          sx={{
            div: {
              width: '100%',
            },
            'counter-reset': 'section',
            li: {
              marginTop: '32px',
            },
            'li li': {
              marginTop: '16px',
            },
            '.legal > ol > p': {
              marginTop: '32px',
            },
            '.legal ol': {
              'counter-reset': 'clause',
              'list-style': 'none outside none',
            },
            '.legal ol li': {
              'counter-increment': 'clause',
              'list-style': 'none outside none',
            },
            '.legal ol li:before': {
              content: `counters(clause,".") ". "`,
              position: 'absolute',
              left: '-50px',
            },
            '.legal ol li li:before': {
              content: `counters(clause,".") ". "`,
              position: 'absolute',
              left: '0px',
            },
            '.legal ol li li': {
              marginLeft: '50px',
            },
            '.legal ol li li li:before': {
              content: `counters(clause,".") ". "`,
              position: 'absolute',
              left: '50px',
            },
            '.legal ol li li li': {
              marginLeft: '75px',
            },
            '.specialgroup span': {
              position: 'absolute',
              left: '-50px',
            },
            '.specialgroup div': {
              marginLeft: '70px',
            },
            '.specialgroup div span': {
              position: 'absolute',
              left: '-70px',
            },
            '.specialgroup p': {
              marginTop: '16px',
            },
            '.specialgroup': {
              marginTop: '16px',
            },
            '.legal > ol > .specialgroup': {
              marginTop: '32px',
            },

            '.annex': {
              textAlign: 'left',
            },

            '.annex ol': {
              listStyleType: 'decimal',
              marginLeft: '0px',
            },
            '.annex ol ol': {
              listStyleType: 'lower-alpha',
              marginLeft: '50px',
            },
          }}
        >
          <div className="legal">
            <ol>
              <p>
                This Government Agency Privacy Statement (“
                <strong>Privacy Statement</strong>”) must be read in conjunction
                with the Terms of Use that accompany the applicable service you
                are requesting from us (the “<strong>Service</strong>”).
              </p>
              <p>
                <u>General</u>
              </p>
              <li>This is a Government agency digital service.</li>
              <li>
                Please note that:
                <ol>
                  <li>
                    We may use “cookies”, where a small data file is sent to
                    your browser to store and track information about you when
                    you enter our digital services. The cookie is used to track
                    information such as the number of users and their frequency
                    of use, profiles of users and their preferred digital
                    services. While this cookie can tell us when you enter our
                    digital services and which pages you visit, it cannot read
                    data off your hard disk.
                  </li>

                  <li>
                    You can choose to accept or decline cookies. Most web
                    browsers automatically accept cookies, but you can usually
                    modify your browser setting to decline cookies if you
                    prefer. This may prevent you from taking full advantage of
                    the digital service.
                  </li>
                  <li>
                    The Service may utilise cookies to facilitate authentication
                    and/or login to the Service. If such cookies are rejected,
                    you might not be able to use the Service.
                  </li>
                </ol>
              </li>
              <li>
                Please see the Annex for any additional terms or information.
              </li>
              <li>
                For the avoidance of doubt, nothing in this Privacy Statement
                shall be construed as limiting or prejudicing our rights at law
                to collect, use or disclose any data without your consent or
                agreement.
              </li>

              <p>
                <u>Use of Data</u>
              </p>
              <li>
                We may request or collect certain types of data from you in
                connection with your access or use of the Service. The data that
                may be requested/collected include those identified in the Annex
                herein. Your data may be stored in our servers, systems or
                devices, in the servers, systems or devices of our third party
                service providers or collaborators, or on your device, and may
                be used by us or our third party service providers or
                collaborators to facilitate your access or use of the Service.
                We or our third party service providers or collaborators may
                collect system configuration information and/or traffic
                information (such as an IP address) and/or use information or
                statistical information to operate, maintain or improve the
                Services or the underlying service of the third party service
                provider or collaborator. For the avoidance of doubt, in this
                Privacy Statement, a reference to a third party service provider
                or collaborator includes other third parties who provide a
                service or collaborate with our third party service provider or
                collaborator.
              </li>

              <li>
                If you provide us with personal data, or where we collect
                personal data from you:
                <ol>
                  <li>
                    We may use, disclose and process the data for any one or
                    more of the following purposes:
                    <ol>
                      <li>
                        to assist, process and facilitate your access or use of
                        the Service;
                      </li>

                      <li>
                        to administer, process and facilitate any transactions
                        or activities by you, whether with us or any other
                        Government Agency or third party service provider or
                        collaborator, and whether for your own benefit, or for
                        the benefit of a third party on whose behalf you are
                        duly authorized to act;
                      </li>

                      <li>
                        to carry out your instructions or respond to any
                        queries, feedback or complaints provided by (or
                        purported to be provided by) you or on your behalf, or
                        otherwise for the purposes of responding to or dealing
                        with your interactions with us;
                      </li>

                      <li>
                        to monitor and track your usage of the Service, to
                        conduct research, data analytics, surveys, market
                        studies and similar activities, in order to assist us in
                        understanding your interests, concerns and preferences
                        and improving the Service (including any service of a
                        third party service provider or collaborator) and other
                        services and products provided by Government Agencies.
                        For the avoidance of doubt, we may also collect, use,
                        disclose and process such information to create reports
                        and produce statistics regarding your transactions with
                        us and your usage of the Services and other services and
                        products provided by Government Agencies for
                        record-keeping and reporting or publication purposes
                        (whether internally or externally);
                      </li>

                      <li>
                        for the purposes of storing or creating backups of your
                        data (whether for contingency or business continuity
                        purposes or otherwise), whether within or outside
                        Singapore;
                      </li>

                      <li>
                        to enable us to contact you or communicate with you on
                        any matters relating to your access or use of the
                        Service, including but not limited to the purposes set
                        out above, via email, push notifications or such other
                        forms of communication that we may introduce from time
                        to time depending on the functionality of the Service
                        and/or your device.
                      </li>
                    </ol>
                  </li>

                  <li>
                    We may share necessary data with other Government Agencies,
                    and third party service providers in connection with the
                    Service, so as to provide the Service to you in the most
                    efficient and effective way unless such sharing is
                    prohibited by law, or to improve the discharge of public
                    functions.
                  </li>

                  <li>
                    We will NOT share your personal data with entities which are
                    not Government Agencies, except where such sharing is
                    necessary for such entities to assist us in providing the
                    Service to you or for fulfilling any of the purposes herein.
                  </li>

                  <li>
                    For your convenience, we may also display to you data you
                    had previously supplied us or other Government Agencies.
                    This will speed up the transaction and save you the trouble
                    of repeating previous submissions. Should the data be
                    out-of-date, please supply us the latest data.
                  </li>
                </ol>
              </li>

              <div className="specialgroup">
                <span>5A.</span> Please note that we may be required to disclose
                your data by law, including any law governing the use/provision
                of any service of a third party service provider or
                collaborator.
              </div>

              <li>
                You may withdraw your consent to the use and disclosure of your
                data by us with reasonable notice and subject to any prevailing
                legal or contractual restrictions; however, doing so may prevent
                the proper functioning of the Service and may also result in the
                cessation of the Service to you.
              </li>

              <p>
                <u>Protection of data</u>
              </p>

              <li>
                To safeguard your personal data, all electronic storage and
                transmission of personal data is secured with appropriate
                security technologies.
              </li>

              <li>
                The Service may contain links to external sites or services
                whose data protection and privacy practices may differ from
                ours. We are not responsible for the content and privacy
                practices of these other websites or services and encourage you
                to consult the privacy notices of those sites or services.
              </li>

              <p>
                <u>Contact information</u>
              </p>

              <li>
                Please{' '}
                <Link isExternal href={FEEDBACK_FORM_URL}>
                  contact us via our Support Form
                </Link>{' '}
                if you:
                <ol>
                  <li>
                    have any enquiries or feedback on our data protection
                    policies and procedures; or
                  </li>
                  <li>
                    need more information on or access to data which you have
                    provided to us directly in the past.
                  </li>
                </ol>
              </li>

              <p>
                <u>Definitions</u>
              </p>

              <li>
                In this Privacy Statement, “Government Agencies” means the
                Government (including its ministries, departments and organs of
                state) and public authorities (such as statutory boards) and
                “personal data” shall have the same meaning as its definition in
                the Personal Data Protection Act 2012 (No. 26 of 2012).
              </li>
            </ol>

            <p style={{ textAlign: 'center', marginTop: '100px' }}>
              This Privacy Policy is dated {updatedDate}.
            </p>
          </div>
        </VStack>
      </PageSection>
    </>
  )
}
